/**
 * Cache Manager Utility
 * Handles cache versioning and auto-refresh
 */

import { getBackendUrl } from './api';

const CACHE_VERSION_KEY = 'app_cache_version';
const CHECK_INTERVAL = 30000; // Check every 30 seconds for faster refresh after content update

class CacheManager {
  constructor() {
    this.currentVersion = null;
    this.checkInterval = null;
    this.listeners = [];
  }

  /**
   * Initialize cache manager
   */
  async init() {
    try {
      // Get initial cache version
      this.currentVersion = await this.fetchCacheVersion();
      
      // Store in localStorage
      if (this.currentVersion) {
        localStorage.setItem(CACHE_VERSION_KEY, this.currentVersion);
      }
      
      // Start periodic checking
      this.startPeriodicCheck();
      
      console.log('✅ Cache Manager initialized - Version:', this.currentVersion);
    } catch (error) {
      console.error('❌ Cache Manager init failed:', error);
    }
  }

  /**
   * Fetch current cache version from server
   */
  async fetchCacheVersion() {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/cache/version`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.cache_version;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching cache version:', error);
      return null;
    }
  }

  /**
   * Check if cache version has changed
   */
  async checkForUpdates() {
    try {
      const serverVersion = await this.fetchCacheVersion();
      
      if (!serverVersion) {
        return false;
      }

      const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
      
      // If versions don't match, cache needs refresh
      if (storedVersion && storedVersion !== serverVersion) {
        console.log('🔄 Cache version changed:', {
          old: storedVersion,
          new: serverVersion
        });
        
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking cache updates:', error);
      return false;
    }
  }

  /**
   * Start periodic cache version checking
   * DISABLED: Auto-refresh disabled per user request
   */
  startPeriodicCheck() {
    // Auto-refresh disabled - user will manually refresh when needed
    console.log('ℹ️ Cache auto-refresh is disabled');
    return;
    
    // Original code commented out:
    // if (this.checkInterval) {
    //   clearInterval(this.checkInterval);
    // }
    // this.checkInterval = setInterval(async () => {
    //   const needsUpdate = await this.checkForUpdates();
    //   if (needsUpdate) {
    //     console.log('📥 Cache update detected - Notifying listeners');
    //     this.notifyListeners();
    //   }
    // }, CHECK_INTERVAL);
  }

  /**
   * Stop periodic checking
   */
  stopPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Add listener for cache updates
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(cb => cb !== callback);
  }

  /**
   * Notify all listeners about cache update
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in cache listener:', error);
      }
    });
  }

  /**
   * Force refresh application
   */
  async forceRefresh() {
    try {
      // Update to latest version
      const latestVersion = await this.fetchCacheVersion();
      if (latestVersion) {
        localStorage.setItem(CACHE_VERSION_KEY, latestVersion);
        this.currentVersion = latestVersion;
      }

      // Clear service worker cache if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('✅ Service worker cache cleared');
      }

      // Reload page
      console.log('🔄 Reloading application...');
      window.location.reload(true);
    } catch (error) {
      console.error('Error during force refresh:', error);
      // Reload anyway
      window.location.reload(true);
    }
  }

  /**
   * Manual cache invalidation (admin only)
   */
  async invalidateCache(reason = 'manual') {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/cache/invalidate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Cache invalidated:', data);
        
        // Update local version
        if (data.new_version) {
          localStorage.setItem(CACHE_VERSION_KEY, data.new_version);
          this.currentVersion = data.new_version;
        }

        return data;
      } else {
        throw new Error('Failed to invalidate cache');
      }
    } catch (error) {
      console.error('Error invalidating cache:', error);
      throw error;
    }
  }
}

// Singleton instance
const cacheManager = new CacheManager();

export default cacheManager;
